import { useState, useRef, useCallback, useEffect } from 'react'
import { useQuery, keepPreviousData } from '@tanstack/react-query'
import { cpSearchDomains } from '@/lib/api/controlPlaneClient'

const DEBOUNCE_MS = 500
const MIN_QUERY_LENGTH = 2

export function useDomainSearch() {
  const [rawQuery, setRawQuery] = useState('')
  const [debouncedQuery, setDebouncedQuery] = useState('')
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Debounce effect
  useEffect(() => {
    if (timerRef.current) clearTimeout(timerRef.current)
    if (rawQuery.length >= MIN_QUERY_LENGTH) {
      timerRef.current = setTimeout(() => setDebouncedQuery(rawQuery), DEBOUNCE_MS)
    } else {
      setDebouncedQuery('')
    }
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current)
    }
  }, [rawQuery])

  const searchNow = useCallback(() => {
    if (rawQuery.length >= MIN_QUERY_LENGTH) {
      if (timerRef.current) clearTimeout(timerRef.current)
      setDebouncedQuery(rawQuery)
    }
  }, [rawQuery])

  const clear = useCallback(() => {
    setRawQuery('')
    setDebouncedQuery('')
    if (timerRef.current) clearTimeout(timerRef.current)
  }, [])

  const query = useQuery({
    queryKey: ['domain-search', debouncedQuery],
    queryFn: () => cpSearchDomains(debouncedQuery),
    enabled: debouncedQuery.length >= MIN_QUERY_LENGTH,
    staleTime: 30_000,
    placeholderData: keepPreviousData,
  })

  return {
    rawQuery,
    setQuery: setRawQuery,
    searchNow,
    clear,
    results: query.data?.results ?? [],
    isSearching: query.isFetching,
    hasSearched: debouncedQuery.length >= MIN_QUERY_LENGTH && !query.isFetching && !query.isError,
    error: query.error?.message ?? null,
  }
}
